import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import type { ApiClient, KundeAkte, LogoCandidate } from "@kundeninfo/api-client";
import { Icon, Modal, ModalFuss, useSofortPopup } from "@kundeninfo/ui";
import { Bildkachel } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { LOGO_TEXT, SOURCE_LABEL } from "./texts.js";
import "./LogoChoice.css";

/** Wert der Keins-Kachel — keine URL, deshalb nie mit einem Vorschlag verwechselbar. */
const NONE = "ohne-logo";
/** Wert der Kachel „Logo behalten“ (vorhandenes Logo) — ebenfalls keine URL. */
const KEEP = "logo-behalten";

/**
 * Die Adresse des hinterlegten Logos eines Geschäftspartners, `null` ohne Logo. Mit der Fassung des Satzes
 * (`?v=`): Ein in den Stammdaten ersetztes Logo behält seinen Dateinamen, und der Browser lädt dieselbe Adresse nicht
 * neu — jedes Speichern rückt die Fassung vor (Entscheid 673). Für den Akten-Kopf und die Kachel „Logo behalten“.
 */
export function customerLogoSrc(k: Pick<KundeAkte, "id" | "version" | "logoFile">): string | null {
  return k.logoFile === null || k.logoFile === undefined ? null : `/api/customers/${k.id}/logo?v=${k.version}`;
}

export type LogoChoiceProps = {
  /** Formularname der Bildkachel (`gp-logo` im Geschäftspartner-Dialog). */
  name: string;
  candidates: readonly LogoCandidate[];
  /** Die im Markenrechts-Dialog bestätigte URL; `null` = keine Wahl. */
  chosen: string | null;
  onChosen(url: string | null): void;
  help: string;
  /** Das vorhandene Logo des Partners (`customerLogoSrc`): Statt „Kein Logo“ steht dann die Kachel „Logo behalten“
   *  mit diesem Bild — sie ändert nichts (einen Weg „Logo entfernen“ gibt es in der Maske nicht, Fixrunde 1 H5). */
  existingLogo?: string | null | undefined;
};

/**
 * Die Logo-Kacheln der Anreicherung (Bildkachel, me-design a9a3e5a, Entscheid 57) samt Markenrechts-Dialog — EINE
 * Stelle für die Anlege-Maske und die Stammdaten (Entscheid 673). Kontrolliert: Ein Vorschlag wird erst nach
 * „Übernehmen“ im Dialog zur Wahl, bis dahin setzt das Element die Kachel auf die bisherige Wahl zurück; die
 * Keins-Kachel gilt sofort. Die Wahl selbst hält der Aufrufer — sie gehört zu seinem Entwurf und geht erst mit dessen
 * Schreibweg an die Logo-Route.
 */
export function LogoChoice(p: LogoChoiceProps): ReactElement {
  /** Der Kandidat, dessen Markenrechts-Dialog gerade offen ist (null = kein Dialog). */
  const [dialog, setDialog] = useState<string | null>(null);
  const existing = p.existingLogo ?? null;
  return (
    <>
      {showsLogoChoice(p.candidates, p.chosen) && (
        <Bildkachel name={p.name} beschriftung={LOGO_TEXT.tiles} hilfe={p.help}
          wert={p.chosen ?? (existing === null ? NONE : KEEP)}
          onGeaendert={(d) => {
            if (d.art === "keins" || d.wert === KEEP) p.onChosen(null);
            else setDialog(d.wert);
          }}>
          {/* Das vorhandene Logo als gewöhnliche Bildkachel (eine Keins-Kachel trägt kein Bild), sonst „Kein Logo“. */}
          {existing === null
            ? <option value={NONE} data-art="keins" data-bildtext={LOGO_TEXT.noneImage} data-quelle="">{LOGO_TEXT.none}</option>
            : <option value={KEEP} data-bild={existing}>{LOGO_TEXT.keep}</option>}
          {p.candidates.map((k, i) => (
            <option key={k.url} value={k.url} data-bild={k.url} data-quelle={SOURCE_LABEL[k.source]}>
              {LOGO_TEXT.candidate(i + 1)}
            </option>
          ))}
          {/* Eine schon übernommene Wahl, die ein neuer Abruf nicht mehr vorschlägt, bleibt sichtbar — sie wird
              weiter übernommen. */}
          {p.chosen !== null && !p.candidates.some((k) => k.url === p.chosen) && (
            <option value={p.chosen} data-bild={p.chosen}>{LOGO_TEXT.taken}</option>
          )}
        </Bildkachel>
      )}

      {dialog !== null && (
        <Modal titel={LOGO_TEXT.dialogTitle} onAbbrechen={() => setDialog(null)}>
          <div className="logo-choice__dialog">
            <img className="logo-choice__image" src={dialog} alt="" />
            <p className="logo-choice__notice">{LOGO_TEXT.trademark}</p>
          </div>
          {/* Eine Wahl ohne Eingabe: Es gibt keinen Entwurf, den ein Abbrechen verlöre — deshalb fragt es hier nicht
              nach (Hausregel „striktes Modalpanel“). */}
          <ModalFuss speichernText={LOGO_TEXT.take}
            onSpeichern={() => { p.onChosen(dialog); setDialog(null); }}
            onAbbrechen={() => setDialog(null)} />
        </Modal>
      )}
    </>
  );
}

/** Schema vorn — wie `normalizeWebsite` in domain mit „://“, sonst gälte „example.de:8080“ als schemabehaftet. */
const SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;
/** Eine Domain: Punkte zwischen Namensteilen und eine Endung aus Buchstaben (oder IDN `xn--…`). „nordlicht“ ist keine. */
const DOMAIN = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:[a-z]{2,}|xn--[a-z0-9-]+)$/i;
/** Eine ausgeschriebene IPv4-Adresse (vier Teile 0–255) — ob das Ziel erlaubt ist, entscheidet die SSRF-Abwehr des
 *  Servers (der E2E-Quellen-Stub steht auf 127.0.0.1). */
const IPV4 = /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)$/;

/**
 * Die Adresse für den Abruf der Logo-Vorschläge — oder `null`, wenn der Text keine Domain und keine ausgeschriebene
 * IPv4-Adresse enthält (dann steht der Knopf still). Die IPv4-Adresse muss wörtlich im Text stehen: „1.5“ machte erst
 * der URL-Parser zu 1.0.0.5. Ohne Schema getippt („nordlicht.example“) geht sie mit https:// hinaus, wie sie der Server beim
 * Speichern ablegt; das FELD bleibt unverändert.
 */
export function websiteForLookup(text: string): string | null {
  const trimmed = text.trim();
  if (trimmed === "" || /\s/.test(trimmed)) return null;
  const address = SCHEME.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(address);
    const host = url.hostname;
    const web = url.protocol === "http:" || url.protocol === "https:";
    return web && (DOMAIN.test(host) || (IPV4.test(host) && trimmed.includes(host))) ? address : null;
  } catch {
    return null;
  }
}

export type WebsiteLogoLookupProps = {
  /** Das Feld „Webseite“ der Maske — der Knopf steht direkt dahinter. */
  children: ReactNode;
  /** Was unter Feld und Statuszeile steht (die Vorschlagsleiste der Anreicherung). */
  below?: ReactNode;
  website: string;
  lookup: ApiClient["lookupEnrichment"];
  /** Die geholten Kandidaten — der Aufrufer hält sie und zeichnet die Kacheln (`LogoChoice`) an seiner Stelle der
   *  Maske; hier werden sie nur gesetzt und mit der Adresse wieder verworfen. */
  candidates: readonly LogoCandidate[];
  onCandidates(candidates: readonly LogoCandidate[]): void;
  chosen: string | null;
  onChosen(url: string | null): void;
};

type Phase = "idle" | "running" | "none" | "failed";

const PHASE_TEXT: Record<Phase, string> = {
  idle: "", running: LOGO_TEXT.fetching, none: LOGO_TEXT.noCandidates, failed: LOGO_TEXT.failed,
};

/** Stehen Kacheln da? Ohne Kandidaten und ohne Wahl zeichnet `LogoChoice` nichts — der Aufrufer spart dann die Zeile. */
export const showsLogoChoice = (candidates: readonly LogoCandidate[], chosen: string | null): boolean =>
  candidates.length > 0 || chosen !== null;

/**
 * „Logo aus der Webseite“ (Entscheid 673, seit 817 in beiden Modi des Geschäftspartner-Dialogs): ein Symbolknopf
 * direkt hinter dem Feld, auf Klick der Abruf allein aus der Webseite (keine Feldvorschläge), darunter ein kurzer Satz
 * (holt, keins, gestört). Die Kacheln zeichnet der Aufrufer (`LogoChoice`) — im Geschäftspartner-Dialog als eigene
 * breite Zeile. Die Kandidaten leben nur, solange der Dialog offen ist: Abbrechen hängt ihn aus, eine Antwort danach
 * verfällt. Sie gelten nur für die Adresse, für die sie geholt wurden: Ändert sich die Webseite, fallen Kacheln, Satz
 * und eine schon gewählte Kachel weg (Fixrunde 1 H1). Der Satz steht in einem Live-Bereich, der von Anfang an im
 * Dialog steht — erst so liest ein Bildschirmleser den Wechsel vor (H9).
 * Während des Speicherns und im fremden Stand sperrt das umgebende Formular (me-formular) den Knopf selbst.
 */
export function WebsiteLogoLookup(p: WebsiteLogoLookupProps): ReactElement {
  const [phase, setPhase] = useState<Phase>("idle");
  /** Die Adresse, für die Kandidaten oder Satz gelten (`null` = noch nichts geholt). */
  const [fetchedFor, setFetchedFor] = useState<string | null>(null);

  const address = websiteForLookup(p.website);
  const running = phase === "running";
  // Vor dem Zeichnen (Layout-Effekt), damit die Kacheln der alten Adresse nicht einen Augenblick stehen bleiben.
  const outdated = !running && fetchedFor !== null && address !== fetchedFor;
  const { chosen, onChosen, onCandidates } = p;
  useLayoutEffect(() => {
    if (!outdated) return;
    onCandidates([]);
    setFetchedFor(null);
    setPhase("idle");
    if (chosen !== null) onChosen(null);
  }, [outdated, chosen, onChosen, onCandidates]);
  const name = running ? LOGO_TEXT.fetching : LOGO_TEXT.fetch;
  const tip = useSofortPopup<HTMLElement>(address === null ? LOGO_TEXT.needsDomain : name);
  const knob = useRef<HTMLElement | null>(null);
  const knobRef = useCallback((el: HTMLElement | null) => { knob.current = el; tip(el); }, [tip]);
  /** Hatte der Knopf beim Klick den Fokus? Abgeschaltet verliert er ihn an body (Fokus-Korrektur des Browsers) —
   *  nach dem Holen bekommt er ihn zurück, sonst begänne die Tastatur wieder oben im Dialog. */
  const refocus = useRef(false);
  useEffect(() => {
    if (running || !refocus.current) return;
    refocus.current = false;
    if (document.activeElement === null || document.activeElement === document.body) knob.current?.focus();
  }, [running]);

  async function fetchLogos(): Promise<void> {
    if (address === null || running) return;
    refocus.current = knob.current !== null && knob.current === document.activeElement;
    setPhase("running");
    try {
      const answer = await p.lookup({ website: address });
      onCandidates(answer.logoCandidates);
      setPhase(answer.logoCandidates.length === 0 ? "none" : "idle");
    } catch {
      setPhase("failed");
    }
    setFetchedFor(address);
  }

  return (
    <div className="logo-lookup">
      <div className="logo-lookup__row">
        {p.children}
        <KiKnopf name={name} deaktiviert={address === null || running} knopfRef={knobRef}
          onClick={() => { void fetchLogos(); }}>
          <Icon name="image" />
        </KiKnopf>
      </div>
      <p className={`logo-lookup__status${phase === "failed" ? " logo-lookup__status--failed" : ""}`}
        role="status" aria-live="polite">{PHASE_TEXT[phase]}</p>
      {p.below}
    </div>
  );
}
